import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';

import { CloudOff } from 'lucide-react';

import { AppShell } from '@/app/AppShell';
import { BrandMark } from '@/app/Brand';
import { useAuthStore } from '@/features/auth/store';
import { useActiveGroupSync, useBootstrap } from '@/features/bootstrap';
import { ExpenseEditorModal } from '@/features/expenses/ExpenseEditorModal';
import { BASE_URL } from '@/lib/api';
import { isReturningVisitor } from '@/lib/session';
import { AuthPage } from '@/pages/AuthPage';
import { CategoriesPage } from '@/pages/CategoriesPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { ExpenseDetailsPage } from '@/pages/ExpenseDetailsPage';
import { ExpensesPage } from '@/pages/ExpensesPage';
import { GroupsPage } from '@/pages/GroupsPage';
import { MemberLedgerPage } from '@/pages/MemberLedgerPage';
import { MembersPage } from '@/pages/MembersPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { ReportsPage } from '@/pages/ReportsPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { SettlePage } from '@/pages/SettlePage';
import { Button, ConfirmHost, LoadingState, Toaster, useThemeSync } from '@/ui';

export function App() {
  useThemeSync();
  const signedIn = useAuthStore((state) => state.session !== null);

  return signedIn ? <SignedInApp /> : <SignedOutApp />;
}

/**
 * Only the account pages exist before sign-in. First-time visitors land on
 * "Create account"; anyone who has signed in on this browser before lands on
 * "Sign in". Either way the Google flow creates the account if it is new.
 */
function SignedOutApp() {
  const landing = isReturningVisitor() ? '/login' : '/register';

  return (
    <Router>
      <Routes>
        <Route path="register" element={<AuthPage mode="register" />} />
        <Route path="login" element={<AuthPage mode="login" />} />
        <Route path="*" element={<Navigate to={landing} replace />} />
      </Routes>
      <Toaster />
    </Router>
  );
}

function SignedInApp() {
  const { ready, error, retry } = useBootstrap();
  const signOut = useAuthStore((state) => state.signOut);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-md rounded-3xl border border-line bg-surface p-8 text-center shadow-pop">
          <span className="mx-auto inline-flex size-14 items-center justify-center rounded-2xl bg-danger/10 text-danger">
            <CloudOff size={26} />
          </span>
          <h1 className="mt-5 text-xl font-bold tracking-tight">Couldn&rsquo;t reach the server</h1>
          <p className="mt-2 text-sm text-muted">{error}</p>
          <p className="mt-3 text-[13px] text-muted">
            All data is loaded from the API at{' '}
            <code className="rounded-md bg-elevated px-1.5 py-0.5 text-[12px]">{BASE_URL}</code>.
            Check that it is running and reachable from this browser.
          </p>
          <Button className="mt-6 w-full" size="lg" onClick={retry}>
            Try again
          </Button>
          <Button className="mt-2 w-full" variant="ghost" onClick={signOut}>
            Sign out
          </Button>
        </div>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center">
        <BrandMark size="lg" className="animate-pulse" />
        <LoadingState label="Loading your data…" />
      </div>
    );
  }

  return (
    <Router>
      <GroupSync />
      <Routes>
        <Route path="register" element={<Navigate to="/" replace />} />
        <Route path="login" element={<Navigate to="/" replace />} />
        <Route element={<AppShell />}>
          <Route index element={<DashboardPage />} />
          <Route path="expenses" element={<ExpensesPage />} />
          <Route path="expenses/:id" element={<ExpenseDetailsPage />} />
          <Route path="settle" element={<SettlePage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="groups" element={<GroupsPage />} />
          <Route path="members" element={<MembersPage />} />
          <Route path="members/:id" element={<MemberLedgerPage />} />
          <Route path="categories" element={<CategoriesPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>

      <ExpenseEditorModal />
      <ConfirmHost />
      <Toaster />
    </Router>
  );
}

/** Split out so the effect sits under the Router, where navigation is available. */
function GroupSync() {
  useActiveGroupSync();
  return null;
}
